import {
  Box,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Typography,
  useTheme,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { fetchPendingInvitations, inviteMembers } from 'app/shared/reducers/settings/settings-organization';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useSearchParams } from 'react-router-dom';
import { settingOrgPermssionProps } from './settings-org-rightcard';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';

const SettingsOrgInvite = ({ hasPermission }: settingOrgPermssionProps) => {
  const [email, setEmail] = useState('');
  const [emails, setEmails] = useState([]);
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [mailValid, setMailValid] = useState(false);
  const [emailError, setEmailError] = useState('');
  const userState = useAppSelector(state => state.authentication.account);
  const isOwnMail = (userState?.email || userState?.login) === email;
  const [isMember, setIsMember] = useState(false);

  const dispatch = useAppDispatch();
  const { members, selectedOrgId } = useAppSelector(state => state.settingsOrganization);

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  useEffect(() => {
    if (members.length > 0) {
      const member = Boolean(members.find((member: any) => email === member.email));
      setIsMember(member);
    }
  }, [members, email]);

  // Clear error message when there are no invalid emails
  useEffect(() => {
    if (invalidEmails.length === 0) {
      setEmailError('');
    }
  }, [invalidEmails]);

  const handleInputChange = event => {
    const rawValue = event.target.value;
    setEmail(rawValue);
    setIsButtonDisabled(false);
    setEmailError('');
  };

  const removeEmail = emailToRemove => {
    setEmails(emails.filter(email => email !== emailToRemove));

    if (email === emailToRemove) {
      setEmail('');
    }
  };

  const handleAddIconClick = () => {
    const inputValue = email.trim();
    if (inputValue) {
      // Split by common separators and filter empty entries
      const emailList = inputValue
        .split(/[,;\s]+/)
        .map(e => e.trim())
        .filter(e => e.length > 0);

      const newValidEmails: string[] = [];
      const newInvalidEmails: string[] = [];

      emailList.forEach(emailToCheck => {
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToCheck);

        if (isValid) {
          if (emailToCheck === (userState?.email || userState?.login)) {
            newInvalidEmails.push(emailToCheck);
          } else if (members.find((m: any) => m.email === emailToCheck)) {
            newInvalidEmails.push(emailToCheck);
          } else if (!emails.includes(emailToCheck)) {
            newValidEmails.push(emailToCheck);
          }
        } else {
          newInvalidEmails.push(emailToCheck);
        }
      });

      // Add new valid emails
      if (newValidEmails.length > 0) {
        setEmails(prev => [...prev, ...newValidEmails]);
      }

      // Add new invalid emails
      if (newInvalidEmails.length > 0) {
        setInvalidEmails(prev => [...prev, ...newInvalidEmails]);
        if (newInvalidEmails.length === 1) {
          if (newInvalidEmails[0] === (userState?.email || userState?.login)) {
            setEmailError('This is your own email address.');
          } else if (members.find((m: any) => m.email === newInvalidEmails[0])) {
            setEmailError('User is already a member');
          } else {
            setEmailError('Invalid email format');
          }
        } else {
          setEmailError(`${newInvalidEmails.length} invalid email found`);
        }
      } else {
        setEmailError('');
      }

      setEmail(''); // clear input
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    // Don't prevent default paste behavior, let it happen normally
    // event.preventDefault();
    // const pasteData = event.clipboardData.getData('text');
    // setEmail(pasteData);
  };

  const handleInviteClick = () => {
    const isValidEmail = email ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) : true;

    if (email || (!email && emails.length > 0))
      if (isMember) {
        setEmailError('User is already a member');
      } else {
        if (isValidEmail) {
          if (isOwnMail) {
            setEmailError('This is your own email address.');
          } else {
            dispatch(inviteMembers({ organizationId: selectedOrgId, emailAddresses: emails.length === 0 ? [email] : emails }))
              .then(() => {
                dispatch(fetchPendingInvitations({ orgId: selectedOrgId }));
                setEmail('');
                setEmails([]);
                setIsButtonDisabled(true);
                setEmailError('');
              })
              .catch(error => {
                toast.error('Failed to send invitations');
              });
          }
        } else {
          setEmailError('Please enter a valid email address.');
        }
      }
  };

  return (
    selectedOrgId &&
    hasPermission && (
      <Grid container spacing={2} pt={1}>
        <Grid item xs={12} md={12}>
          <Typography
            variant="h6"
            px={1}
            sx={{ color: theme.palette.mode === 'dark' ? '#ffff' : '#2a2a2a', fontSize: '14px', fontWeight: 600 }}
          >
            Invite
          </Typography>
        </Grid>
        <Grid item xs={12} md={12} className=" p-0 mx-2">
          <FormControl sx={{ m: 1, width: '100%' }} variant="outlined">
            <OutlinedInput
              id="outlined-adornment-weight"
              sx={{
                height: '40px',
                fontSize: '0.75rem',
              }}
              onChange={handleInputChange}
              value={email}
              onKeyDown={event => {
                if (event.key === 'Enter' && email.trim()) {
                  event.preventDefault();
                  handleAddIconClick();
                }
              }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton aria-label="add" onClick={handleAddIconClick}>
                    <AddCircleOutline sx={{ color: 'primary.main' }} />
                  </IconButton>
                </InputAdornment>
              }
              aria-describedby="outlined-weight-helper-text"
              inputProps={{
                'aria-label': 'weight',
              }}
            />

            <Grid container spacing={0.5} sx={{ marginTop: '2px' }}>
              {emails.map((email, index) => (
                <Grid item key={index}>
                  <Chip key={index} label={email} onDelete={() => removeEmail(email)} />
                </Grid>
              ))}
              {invalidEmails.map((email, index) => (
                <Grid item key={`invalid-${index}`}>
                  <Chip
                    label={email}
                    onDelete={() => {
                      setInvalidEmails(prev => {
                        const newInvalidEmails = prev.filter(e => e !== email);
                        if (newInvalidEmails.length === 0) {
                          setEmailError(''); // Clear error message when all invalid emails are removed
                        }
                        return newInvalidEmails;
                      });
                    }}
                    style={{ backgroundColor: '#ffe6e6', color: 'red', border: '1px solid red' }}
                  />
                </Grid>
              ))}
            </Grid>
            <Grid
              container
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: 1,
              }}
            >
              <Grid item xs={12}>
                {invalidEmails.length > 0 && (
                  <Typography color="error" variant="caption">
                    Invalid email: {invalidEmails.join(', ')}
                  </Typography>
                )}
                {isOwnMail && (
                  <Typography color="error" variant="caption">
                    Self-Invitations not allowed. Please use a different email address.
                  </Typography>
                )}
              </Grid>
              <FormHelperText id="outlined-weight-helper-text">Invite your friends through email.</FormHelperText>
              <Button
                onClick={handleInviteClick}
                disabled={invalidEmails.length > 0 || (emails.length === 0 && !email)}
                variant="text"
                disableRipple
                sx={{
                  gap: 1,
                  '&.MuiButtonBase-root:hover': {
                    bgcolor: 'transparent',
                  },
                }}
              >
                <Typography
                  sx={{
                    color: '#F6F7F8',
                    textTransform: 'none',
                    borderRadius: 1.875,
                    py: 0.5,
                    px: 3.25,
                    border: `1px solid`,
                    borderColor: 'primary.light',
                    bgcolor: 'primary.light',
                    fontSize: theme.infoText.regular.fontSize,
                    fontWeight: theme.infoText.regular.fontWeight,
                    fontStyle: theme.infoText.regular.fontStyle,
                  }}
                >
                  Invite
                </Typography>
              </Button>
            </Grid>
          </FormControl>
        </Grid>
      </Grid>
    )
  );
};

export default SettingsOrgInvite;
